import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FaCheckCircle, FaCog, FaCrown, FaPlus } from 'react-icons/fa';
import { Button } from 'components/ui/button';
import { Card } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { Badge } from 'components/ui/badge';

const DEFAULT_WIDGET_CONFIG = {
  paymentsEnabled: false,
  publishPlans: false,
  plans: [
    { id: 'starter', name: 'Starter', price: 49, period: 'mes', description: 'Para empezar', highlighted: false },
    { id: 'growth', name: 'Growth', price: 99, period: 'mes', description: 'El más popular', highlighted: true },
    { id: 'scale', name: 'Scale', price: 199, period: 'mes', description: 'Para equipos grandes', highlighted: false },
  ],
};

function AdminPlanesPagos() {
  const isAdminUser = localStorage.getItem('isAdmin') === 'true';
  const [error, setError] = useState('');
  const [copyMessage, setCopyMessage] = useState('');
  const [savingBookingConfig, setSavingBookingConfig] = useState(false);
  const [bookingConfig, setBookingConfig] = useState(DEFAULT_WIDGET_CONFIG);
  const [isStripeConfigModalOpen, setIsStripeConfigModalOpen] = useState(false);
  const [stripeSecretKey, setStripeSecretKey] = useState('');
  const [hasStripeConfig, setHasStripeConfig] = useState(false);
  const [paymenterConfig, setPaymenterConfig] = useState({
    active: false,
    baseUrl: '',
    apiKey: '',
    webhookSecret: '',
    planMapText: '{\n  "starter": "starter",\n  "growth": "growth",\n  "scale": "scale"\n}',
  });
  const [savingPaymenter, setSavingPaymenter] = useState(false);
  const [paymenterTestResult, setPaymenterTestResult] = useState('');

  const getAuthToken = () => sessionStorage.getItem('shortToken') || localStorage.getItem('shortToken');

  const updatePlanField = (index, key, value) => {
    setBookingConfig((prev) => {
      const plans = [...(prev.plans || [])];
      plans[index] = { ...(plans[index] || {}), [key]: value };
      return { ...prev, plans };
    });
  };

  const addPlan = () => {
    setBookingConfig((prev) => ({
      ...prev,
      plans: [
        ...(prev.plans || []),
        {
          id: `plan_${Date.now()}`,
          name: 'Nuevo plan',
          price: 0,
          period: 'mes',
          description: '',
          highlighted: false,
        },
      ].slice(0, 6),
    }));
  };

  const removePlan = (index) => {
    setBookingConfig((prev) => ({
      ...prev,
      plans: (prev.plans || []).filter((_, i) => i !== index),
    }));
  };

  const checkStripeConfig = async () => {
    try {
      const token = getAuthToken();
      if (!token) return;
      const response = await axios.get('/api/servicios/stripe-config', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHasStripeConfig(!!response.data.hasStripeConfig);
    } catch (err) {
      console.error('Error al verificar Stripe:', err);
    }
  };

  const fetchBookingConfig = async () => {
    try {
      const token = getAuthToken();
      if (!token) return;
      const response = await axios.get('/api/servicios/booking-config', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const cfg = response.data?.configuracion || {};
      setBookingConfig((prev) => ({
        ...prev,
        ...cfg,
        paymentsEnabled: typeof cfg.paymentsEnabled === 'boolean' ? cfg.paymentsEnabled : prev.paymentsEnabled,
        publishPlans: typeof cfg.publishPlans === 'boolean' ? cfg.publishPlans : prev.publishPlans,
        plans: Array.isArray(cfg.plans) && cfg.plans.length > 0 ? cfg.plans : prev.plans,
      }));
    } catch (err) {
      console.error('Error al obtener configuración de planes:', err);
    }
  };

  const saveBookingConfig = async () => {
    try {
      const token = getAuthToken();
      if (!token) throw new Error('No se encontró token de autenticación');
      setSavingBookingConfig(true);
      await axios.put(
        '/api/servicios/booking-config',
        { activo: true, configuracion: bookingConfig },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCopyMessage('Planes guardados');
      setTimeout(() => setCopyMessage(''), 2200);
    } catch (err) {
      setError(`No se pudo guardar planes: ${err.response?.data?.error || err.message}`);
    } finally {
      setSavingBookingConfig(false);
    }
  };

  const handleStripeConfigSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = getAuthToken();
      if (!token) throw new Error('No se encontró el token de autenticación');
      await axios.post(
        '/api/servicios/stripe-config',
        { stripeSecretKey },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setHasStripeConfig(true);
      setIsStripeConfigModalOpen(false);
      setCopyMessage('Stripe configurado correctamente');
      setTimeout(() => setCopyMessage(''), 2200);
    } catch (err) {
      setError(`Error al configurar Stripe: ${err.response?.data?.error || err.message}`);
    }
  };

  const loadPaymenterConfig = async () => {
    try {
      const token = getAuthToken();
      if (!token) return;
      const response = await axios.get('/api/paymenter/config', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const cfg = response.data?.config || {};
      setPaymenterConfig((prev) => ({
        ...prev,
        active: !!response.data?.active,
        baseUrl: cfg.baseUrl || '',
        apiKey: '',
        webhookSecret: '',
        planMapText: JSON.stringify(cfg.planMap || { starter: 'starter', growth: 'growth', scale: 'scale' }, null, 2),
      }));
    } catch (err) {
      console.error('Error al cargar Paymenter config:', err);
    }
  };

  const savePaymenterConfig = async () => {
    try {
      const token = getAuthToken();
      if (!token) throw new Error('No se encontró token de autenticación');
      setSavingPaymenter(true);
      setPaymenterTestResult('');
      let planMap = {};
      try {
        planMap = JSON.parse(paymenterConfig.planMapText || '{}');
      } catch (parseError) {
        throw new Error('Plan map inválido: debe ser JSON válido');
      }

      await axios.put(
        '/api/paymenter/config',
        {
          active: !!paymenterConfig.active,
          baseUrl: paymenterConfig.baseUrl,
          apiKey: paymenterConfig.apiKey,
          webhookSecret: paymenterConfig.webhookSecret,
          planMap,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setCopyMessage('Configuración de Paymenter guardada');
      setTimeout(() => setCopyMessage(''), 2200);
      await loadPaymenterConfig();
    } catch (err) {
      setError(`No se pudo guardar Paymenter: ${err.response?.data?.error || err.message}`);
    } finally {
      setSavingPaymenter(false);
    }
  };

  const testPaymenterIntegration = async () => {
    try {
      const token = getAuthToken();
      if (!token) throw new Error('No se encontró token');
      const response = await axios.get('/api/paymenter/accounts', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const total = Array.isArray(response.data?.accounts) ? response.data.accounts.length : 0;
      setPaymenterTestResult(`OK: módulo activo, cuentas sincronizadas: ${total}`);
    } catch (err) {
      setPaymenterTestResult(`Error: ${err.response?.data?.error || err.message}`);
    }
  };

  useEffect(() => {
    if (!isAdminUser) return;
    checkStripeConfig();
    fetchBookingConfig();
    loadPaymenterConfig();
  }, [isAdminUser]);

  if (!isAdminUser) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-8">
        <Card className="rounded-2xl p-6">
          <p className="text-sm text-red-600">Acceso denegado. Solo administradores.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <div
        className="mb-6 rounded-2xl p-6 text-white shadow-lg"
        style={{ background: 'linear-gradient(105deg, #020617 0%, #0f172a 55%, #1d4ed8 100%)' }}
      >
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="flex items-center text-3xl font-bold">
              <FaCrown className="mr-3 text-amber-300" />
              Planes y Pagos
            </h1>
            <p className="mt-1 text-sm text-blue-100">Gestiona monetización, Stripe y planes públicos.</p>
          </div>
          <Button
            onClick={() => setIsStripeConfigModalOpen(true)}
            variant="secondary"
            className="rounded-xl border border-blue-200/60 bg-blue-800/70 text-white hover:bg-blue-700/80 hover:text-white"
          >
            <FaCog className="mr-2 text-blue-200" />
            Stripe {hasStripeConfig ? 'conectado' : 'pendiente'}
          </Button>
        </div>
      </div>

      {error ? (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      ) : null}
      {copyMessage ? (
        <div className="mb-4 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">{copyMessage}</div>
      ) : null}

      <Card className="rounded-2xl border-emerald-200 p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <h2 className="flex items-center text-xl font-semibold text-slate-900">
            <FaCrown className="mr-2 text-amber-500" />
            Monetización y planes
          </h2>
          <Badge variant={hasStripeConfig ? 'success' : 'default'}>
            Stripe {hasStripeConfig ? 'conectado' : 'pendiente'}
          </Badge>
        </div>

        <div className="mb-4 grid grid-cols-1 gap-2 md:grid-cols-2">
          <label className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={bookingConfig.paymentsEnabled}
              onChange={(e) => setBookingConfig((prev) => ({ ...prev, paymentsEnabled: e.target.checked }))}
              disabled={!hasStripeConfig}
            />
            Activar cobros online
          </label>
          <label className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={bookingConfig.publishPlans}
              onChange={(e) => setBookingConfig((prev) => ({ ...prev, publishPlans: e.target.checked }))}
            />
            Publicar planes en landing/widget
          </label>
        </div>

        {!hasStripeConfig ? (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700">
            Configura Stripe para habilitar cobros y vender servicios desde el panel.
          </div>
        ) : null}

        <div className="space-y-3">
          {(bookingConfig.plans || []).map((plan, index) => (
            <div key={plan.id || index} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <div className="grid grid-cols-1 gap-2 md:grid-cols-5">
                <Input value={plan.name || ''} onChange={(e) => updatePlanField(index, 'name', e.target.value)} placeholder="Nombre del plan" />
                <Input type="number" min="0" step="1" value={plan.price ?? 0} onChange={(e) => updatePlanField(index, 'price', Number(e.target.value || 0))} placeholder="Precio" />
                <Input value={plan.period || 'mes'} onChange={(e) => updatePlanField(index, 'period', e.target.value)} placeholder="Periodo (mes)" />
                <Input value={plan.description || ''} onChange={(e) => updatePlanField(index, 'description', e.target.value)} placeholder="Descripción breve" />
                <div className="flex items-center gap-2">
                  <label className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700">
                    <input type="checkbox" checked={!!plan.highlighted} onChange={(e) => updatePlanField(index, 'highlighted', e.target.checked)} />
                    Destacado
                  </label>
                  <Button type="button" variant="danger" size="sm" onClick={() => removePlan(index)}>
                    Quitar
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          <Button type="button" variant="secondary" onClick={addPlan} disabled={(bookingConfig.plans || []).length >= 6}>
            <FaPlus className="mr-2" />
            Añadir plan
          </Button>
          <Button type="button" onClick={saveBookingConfig} disabled={savingBookingConfig}>
            <FaCheckCircle className="mr-2" />
            {savingBookingConfig ? 'Guardando...' : 'Guardar planes'}
          </Button>
        </div>
      </Card>

      <Card className="mt-6 rounded-2xl border-blue-200 p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <h2 className="flex items-center text-xl font-semibold text-slate-900">
            <FaCog className="mr-2 text-blue-600" />
            Paymenter (Facturación y aprovisionamiento)
          </h2>
          <Badge variant={paymenterConfig.active ? 'success' : 'default'}>
            {paymenterConfig.active ? 'Activo' : 'Inactivo'}
          </Badge>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <label className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={paymenterConfig.active}
              onChange={(e) => setPaymenterConfig((prev) => ({ ...prev, active: e.target.checked }))}
            />
            Activar integración Paymenter
          </label>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-semibold text-slate-700">URL Paymenter</label>
            <Input
              type="text"
              value={paymenterConfig.baseUrl}
              onChange={(e) => setPaymenterConfig((prev) => ({ ...prev, baseUrl: e.target.value }))}
              placeholder="https://billing.tudominio.com"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-semibold text-slate-700">API Key Paymenter</label>
            <Input
              type="password"
              value={paymenterConfig.apiKey}
              onChange={(e) => setPaymenterConfig((prev) => ({ ...prev, apiKey: e.target.value }))}
              placeholder="Solo se actualiza si introduces valor"
            />
          </div>
          <div className="md:col-span-2">
            <label className="mb-1 block text-sm font-semibold text-slate-700">Webhook Secret</label>
            <Input
              type="password"
              value={paymenterConfig.webhookSecret}
              onChange={(e) => setPaymenterConfig((prev) => ({ ...prev, webhookSecret: e.target.value }))}
              placeholder="Firma HMAC de webhooks"
            />
          </div>
          <div className="md:col-span-2">
            <label className="mb-1 block text-sm font-semibold text-slate-700">Plan map (JSON)</label>
            <textarea
              rows={6}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-blue-500 focus:outline-none"
              value={paymenterConfig.planMapText}
              onChange={(e) => setPaymenterConfig((prev) => ({ ...prev, planMapText: e.target.value }))}
            />
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <Button type="button" onClick={savePaymenterConfig} disabled={savingPaymenter}>
            <FaCheckCircle className="mr-2" />
            {savingPaymenter ? 'Guardando...' : 'Guardar Paymenter'}
          </Button>
          <Button type="button" variant="secondary" onClick={testPaymenterIntegration}>
            Probar integración
          </Button>
        </div>
        {paymenterTestResult ? (
          <p className="mt-3 text-sm text-slate-600">{paymenterTestResult}</p>
        ) : null}
      </Card>

      {isStripeConfigModalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="mb-1 text-2xl font-bold text-slate-900">Configurar Stripe</h3>
            <p className="mb-4 text-sm text-slate-500">Introduce o reemplaza tu clave secreta.</p>
            <form onSubmit={handleStripeConfigSubmit} className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-700">Clave secreta</label>
                <Input
                  type="password"
                  value={stripeSecretKey}
                  onChange={(e) => setStripeSecretKey(e.target.value)}
                  placeholder={hasStripeConfig ? 'Introduce una nueva clave para reemplazar la actual' : 'sk_live_... o sk_test_...'}
                  required
                />
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button type="button" onClick={() => setIsStripeConfigModalOpen(false)} variant="secondary">
                  Cancelar
                </Button>
                <Button type="submit" variant="primary">
                  Guardar
                </Button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default AdminPlanesPagos;
